import { useState, useEffect, useCallback, useMemo } from 'react';

// useCart — the customer basket for the product shop. Stores a minimal
// [{ id, qty }] list (product details are joined in at render time from the
// loaded catalogue) and persists to localStorage so the basket survives a
// reopen and the sign-in round-trip at checkout.

export const CART_KEY = 'beu_product_cart';

const readCart = () => {
  try {
    const raw = localStorage.getItem(CART_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((i) => i && i.id && i.qty > 0) : [];
  } catch (e) { return []; }
};

// A cart line is (product, variant) — the same product in two sizes is two lines.
const lineKey = (i) => `${i.id}:${i.variantId || ''}`;

const useCart = () => {
  const [items, setItems] = useState(readCart);

  useEffect(() => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(items)); } catch (e) { /* ignore */ }
  }, [items]);

  const addItem = useCallback((id, qty = 1, variantId = null) => {
    setItems((prev) => {
      const key = `${id}:${variantId || ''}`;
      const found = prev.find((i) => lineKey(i) === key);
      if (found) return prev.map((i) => (lineKey(i) === key ? { ...i, qty: i.qty + qty } : i));
      return [...prev, { id, qty, variantId: variantId || undefined }];
    });
  }, []);

  const setQty = useCallback((id, qty, variantId = null) => {
    const key = `${id}:${variantId || ''}`;
    setItems((prev) => (qty <= 0
      ? prev.filter((i) => lineKey(i) !== key)
      : prev.map((i) => (lineKey(i) === key ? { ...i, qty } : i))));
  }, []);

  const removeItem = useCallback((id, variantId = null) => {
    const key = `${id}:${variantId || ''}`;
    setItems((prev) => prev.filter((i) => lineKey(i) !== key));
  }, []);
  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  // Join cart items with the loaded catalogue → renderable lines + subtotal.
  const build = useCallback((products) => {
    const byId = new Map((products || []).map((p) => [p.id, p]));
    const lines = items
      .map((i) => {
        const p = byId.get(i.id);
        if (!p) return null;
        const variant = i.variantId ? (p.variants || []).find((v) => String(v.id) === String(i.variantId)) : null;
        if (i.variantId && !variant) return null; // variant removed from the product
        const price = variant ? variant.price : p.price;
        const variantLabel = variant
          ? (p.options || []).map((o) => variant.optionValues[o.name]).filter(Boolean).join(' / ')
          : '';
        // A variant line's stock state is the variant's, not the product's.
        const inStock = variant ? variant.inStock !== false : p.inStock;
        return { ...p, price, inStock, qty: i.qty, variantId: i.variantId || null, variantLabel, variantImage: (variant && variant.image) || null, lineTotal: price * i.qty };
      })
      .filter(Boolean);
    const subtotal = lines.reduce((s, l) => s + l.lineTotal, 0);
    return { lines, subtotal };
  }, [items]);

  return { items, count, addItem, setQty, removeItem, clear, build };
};

export default useCart;
